import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuth } from '../lib/auth/auth-context';
import { setPasswordSchema, type SetPasswordInput } from '../lib/auth/schemas';
import { friendlyAuthError } from '../lib/auth/errors';
import { AuthShell, ErrorAlert } from '../components/auth/AuthShell';
import { Field } from '../components/ui/Field';
import { Button } from '../components/ui/Button';

export const Route = createFileRoute('/reset-password')({
  component: ResetPasswordPage,
});

// Reset flow:
// Supabase sends an email containing a link of the form
//   https://app.lynrx.co/reset-password#access_token=...&type=recovery
// supabase-js auto-consumes the hash on page load and creates a session.
// We then prompt for a new password and call updateUser({ password }).
//
// If the user lands here without a session (link expired or pasted from
// memory), we surface a clear "request a fresh link" path rather than a
// silent failure.
function ResetPasswordPage(): JSX.Element {
  const { setPassword, isReady, session, signOut } = useAuth();
  const navigate = useNavigate();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<SetPasswordInput>({
    resolver: zodResolver(setPasswordSchema),
    defaultValues: { password: '', confirmPassword: '' },
  });

  // After a successful reset, the user is signed in by virtue of the
  // recovery token. Hold for a moment so they see the confirmation, then
  // route them to the dashboard.
  useEffect(() => {
    if (!success) return;
    const t = window.setTimeout(() => {
      void navigate({ to: '/' });
    }, 800);
    return () => window.clearTimeout(t);
  }, [success, navigate]);

  if (!isReady) {
    return (
      <AuthShell title="Reset your password">
        <p className="text-sm text-ink-2">Loading…</p>
      </AuthShell>
    );
  }

  if (!session) {
    return (
      <AuthShell
        title="Link expired"
        footer={
          <Link to="/forgot-password" className="text-primary hover:underline">
            Request a new reset link
          </Link>
        }
      >
        <p className="text-sm text-ink-2">
          This reset link is no longer valid. Request a fresh one and follow the email within an
          hour.
        </p>
      </AuthShell>
    );
  }

  const onSubmit = handleSubmit(async (values) => {
    setSubmitError(null);
    const { error } = await setPassword(values.password);
    if (error) {
      setSubmitError(friendlyAuthError('set-password', error));
      return;
    }
    // Sign the recovery session out so the user re-authenticates with
    // the new password — recovery sessions are short-lived by design.
    await signOut();
    setSuccess(true);
    void navigate({ to: '/login' });
  });

  return (
    <AuthShell title="Set a new password" {...(session.user.email ? { subtitle: session.user.email } : {})}>
      <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        {submitError ? <ErrorAlert>{submitError}</ErrorAlert> : null}
        <Field
          label="New password"
          type="password"
          autoComplete="new-password"
          autoFocus
          hint="At least 12 characters."
          error={errors.password?.message}
          {...register('password')}
        />
        <Field
          label="Confirm password"
          type="password"
          autoComplete="new-password"
          error={errors.confirmPassword?.message}
          {...register('confirmPassword')}
        />
        <Button type="submit" loading={isSubmitting} fullWidth>
          Update password
        </Button>
      </form>
    </AuthShell>
  );
}
